/* global axios */
import { subDays, startOfMonth, endOfMonth, startOfDay, endOfDay, format } from 'date-fns';

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

const state = {
  summaryData: null,
  detailsData: null,

  drillDownLevel: 1,
  selectedCampaign: null,
  selectedAd: null,

  // `ad_id` pins the report to one ad (ads are identified by id; two ads may share
  // a name). `status`: active | deleted | all (deleted ads keep their history).
  filters: {
    inbox_id: null,
    campaign: null,
    ads_name: null,
    ad_id: null,
    status: 'active',
    has_appointment: null,
    platform_code: null,
    ads_setting: null,
  },

  dateRange: {
    start_date: null,
    end_date: null,
  },
  dateRangePreset: 'last30days',

  currentPage: 1,
  pageLimit: 50,

  platformCodeOptions: [],
  adsSettingOptions: [],
  isLoadingFilterOptions: false,

  isLoading: false,
  isError: false,
  errorMessage: null,
};

const getters = {
  getSummaryData: state => state.summaryData,
  getDetailsData: state => state.detailsData,
  getDrillDownLevel: state => state.drillDownLevel,
  getSelectedCampaign: state => state.selectedCampaign,
  getSelectedAd: state => state.selectedAd,
  getFilters: state => state.filters,
  getDateRange: state => state.dateRange,
  getDateRangePreset: state => state.dateRangePreset,
  getCurrentPage: state => state.currentPage,
  getPageLimit: state => state.pageLimit,
  getTotalPages: state => {
    if (!state.detailsData || !state.detailsData.total) return 0;
    return Math.ceil(state.detailsData.total / state.pageLimit);
  },
  isLoading: state => state.isLoading,
  isError: state => state.isError,
  getErrorMessage: state => state.errorMessage,
  getPlatformCodeOptions: state => state.platformCodeOptions,
  getAdsSettingOptions: state => state.adsSettingOptions,
  isLoadingFilterOptions: state => state.isLoadingFilterOptions,

  getAggregatedCampaigns: state => {
    if (!state.summaryData || !state.summaryData.by_campaign) return [];

    const campaigns = {};
    state.summaryData.by_campaign.forEach(item => {
      if (!campaigns[item.campaign]) {
        campaigns[item.campaign] = {
          campaign: item.campaign,
          total_conversations: 0,
          total_appointments: 0,
          ad_count: 0,
        };
      }
      campaigns[item.campaign].total_conversations += item.total_conversations;
      campaigns[item.campaign].total_appointments += item.total_appointments;
      campaigns[item.campaign].ad_count += 1;
    });

    const campaignsArray = Object.values(campaigns).map(c => ({
      ...c,
      conversion_rate: c.total_conversations > 0
        ? ((c.total_appointments / c.total_conversations) * 100).toFixed(2)
        : 0,
    }));

    return campaignsArray.sort((a, b) => b.total_conversations - a.total_conversations);
  },
};

const mutations = {
  SET_SUMMARY_DATA(state, payload) { state.summaryData = payload; },
  SET_DETAILS_DATA(state, payload) { state.detailsData = payload; },
  SET_DRILL_DOWN_LEVEL(state, level) { state.drillDownLevel = level; },
  SET_SELECTED_CAMPAIGN(state, campaign) { state.selectedCampaign = campaign; },
  SET_SELECTED_AD(state, ad) { state.selectedAd = ad; },
  SET_FILTERS(state, payload) { state.filters = { ...state.filters, ...payload }; },
  SET_DATE_RANGE(state, payload) { state.dateRange = { ...state.dateRange, ...payload }; },
  SET_DATE_RANGE_PRESET(state, preset) { state.dateRangePreset = preset; },
  SET_CURRENT_PAGE(state, page) { state.currentPage = page; },
  SET_PAGE_LIMIT(state, limit) { state.pageLimit = limit; },
  SET_IS_LOADING(state, isLoading) { state.isLoading = isLoading; },
  SET_IS_ERROR(state, isError) { state.isError = isError; },
  SET_ERROR_MESSAGE(state, message) { state.errorMessage = message; },
  SET_PLATFORM_CODE_OPTIONS(state, options) { state.platformCodeOptions = options; },
  SET_ADS_SETTING_OPTIONS(state, options) { state.adsSettingOptions = options; },
  SET_IS_LOADING_FILTER_OPTIONS(state, isLoading) { state.isLoadingFilterOptions = isLoading; },
  RESET_DRILL_DOWN(state) {
    state.drillDownLevel = 1;
    state.selectedCampaign = null;
    state.selectedAd = null;
  },
  RESET_FILTERS(state) {
    state.filters = {
      inbox_id: null, campaign: null, ads_name: null, ad_id: null, status: 'active',
      has_appointment: null, platform_code: null, ads_setting: null,
    };
  },
};

const actions = {
  async fetchFilterOptions({ rootGetters, commit, state }) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('SET_IS_LOADING_FILTER_OPTIONS', true);

    try {
      const params = { ...state.dateRange };
      if (state.filters.inbox_id) params.inbox_id = state.filters.inbox_id;
      if (state.filters.status) params.status = state.filters.status;
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) delete params[key];
      });

      const res = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/ads/filter-options`),
        { params }
      );

      commit('SET_PLATFORM_CODE_OPTIONS', res.data.platform_codes || []);
      commit('SET_ADS_SETTING_OPTIONS', res.data.ads_settings || []);
      return res.data;
    } catch (error) {
      console.error('Fetch filter options failed:', error);
      commit('SET_PLATFORM_CODE_OPTIONS', []);
      commit('SET_ADS_SETTING_OPTIONS', []);
      throw error;
    } finally {
      commit('SET_IS_LOADING_FILTER_OPTIONS', false);
    }
  },

  async fetchSummaryData({ rootGetters, commit, state }) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('SET_IS_LOADING', true);
    commit('SET_IS_ERROR', false);
    commit('SET_ERROR_MESSAGE', null);

    try {
      const params = { view: 'summary', ...state.filters, ...state.dateRange };
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) delete params[key];
      });

      const res = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/ads-analytics`),
        { params }
      );
      commit('SET_SUMMARY_DATA', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch ads analytics summary failed:', error);
      commit('SET_IS_ERROR', true);
      commit('SET_ERROR_MESSAGE', error.response?.data?.error || 'Failed to fetch analytics data');
      throw error;
    } finally {
      commit('SET_IS_LOADING', false);
    }
  },

  async fetchDetailsData({ rootGetters, commit, state }, page = 1) {
    const accountId = rootGetters.getCurrentAccountId;
    commit('SET_IS_LOADING', true);
    commit('SET_IS_ERROR', false);
    commit('SET_ERROR_MESSAGE', null);

    try {
      const params = { view: 'details', page, limit: state.pageLimit, ...state.filters, ...state.dateRange };
      Object.keys(params).forEach(key => {
        if (params[key] === null || params[key] === undefined) delete params[key];
      });

      const res = await axios.get(
        knowledgeProxy(accountId, `/chatbot/accounts/${accountId}/ads-analytics`),
        { params }
      );
      commit('SET_DETAILS_DATA', res.data);
      commit('SET_CURRENT_PAGE', page);
      return res.data;
    } catch (error) {
      console.error('Fetch ads analytics details failed:', error);
      commit('SET_IS_ERROR', true);
      commit('SET_ERROR_MESSAGE', error.response?.data?.error || 'Failed to fetch conversation details');
      throw error;
    } finally {
      commit('SET_IS_LOADING', false);
    }
  },

  applyDateRangePreset({ commit }, preset) {
    const today = new Date();
    let startDate, endDate;

    switch (preset) {
      case 'today':
        startDate = format(startOfDay(today), 'yyyy-MM-dd');
        endDate = format(endOfDay(today), 'yyyy-MM-dd');
        break;
      case 'last7days':
        startDate = format(startOfDay(subDays(today, 6)), 'yyyy-MM-dd');
        endDate = format(endOfDay(today), 'yyyy-MM-dd');
        break;
      case 'last30days':
        startDate = format(startOfDay(subDays(today, 29)), 'yyyy-MM-dd');
        endDate = format(endOfDay(today), 'yyyy-MM-dd');
        break;
      case 'thismonth':
        startDate = format(startOfMonth(today), 'yyyy-MM-dd');
        endDate = format(endOfDay(today), 'yyyy-MM-dd');
        break;
      case 'lastmonth': {
        const lastMonthStart = startOfMonth(subDays(startOfMonth(today), 1));
        const lastMonthEnd = endOfMonth(subDays(startOfMonth(today), 1));
        startDate = format(lastMonthStart, 'yyyy-MM-dd');
        endDate = format(lastMonthEnd, 'yyyy-MM-dd');
        break;
      }
      default:
        return;
    }

    commit('SET_DATE_RANGE', { start_date: startDate, end_date: endDate });
    commit('SET_DATE_RANGE_PRESET', preset);
  },

  async drillDownToCampaign({ commit, dispatch }, campaignName) {
    commit('SET_DRILL_DOWN_LEVEL', 2);
    commit('SET_SELECTED_CAMPAIGN', campaignName);
    commit('SET_SELECTED_AD', null);
    commit('SET_FILTERS', { campaign: campaignName, ads_name: null, ad_id: null });
    await dispatch('fetchSummaryData');
  },

  // `ad` is either an ad row ({ ad_id, ads_name }) from the report table, or just a
  // name (the Ads Name dropdown), in which case the report filters by name only.
  async drillDownToAd({ commit, dispatch }, ad) {
    const adName = typeof ad === 'string' ? ad : ad.ads_name;
    const adId = typeof ad === 'string' ? null : ad.ad_id || null;
    commit('SET_DRILL_DOWN_LEVEL', 3);
    commit('SET_SELECTED_AD', adName);
    commit('SET_FILTERS', { ads_name: adName, ad_id: adId });
    commit('SET_CURRENT_PAGE', 1);
    await dispatch('fetchDetailsData', 1);
  },

  async goBack({ state, commit, dispatch }) {
    if (state.drillDownLevel === 3) {
      commit('SET_DRILL_DOWN_LEVEL', 2);
      commit('SET_SELECTED_AD', null);
      commit('SET_FILTERS', { ads_name: null, ad_id: null });
      await dispatch('fetchSummaryData');
    } else if (state.drillDownLevel === 2) {
      commit('SET_DRILL_DOWN_LEVEL', 1);
      commit('SET_SELECTED_CAMPAIGN', null);
      commit('SET_FILTERS', { campaign: null, ads_name: null });
      await dispatch('fetchSummaryData');
    }
  },

  async updateFiltersAndFetch({ commit, dispatch, state }, newFilters) {
    commit('SET_FILTERS', newFilters);
    if (state.drillDownLevel === 3) {
      await dispatch('fetchDetailsData', 1);
    } else {
      await dispatch('fetchSummaryData');
    }
  },

  async updateDateRangeAndFetch({ commit, dispatch, state }, { startDate, endDate, preset = 'custom' }) {
    commit('SET_DATE_RANGE', { start_date: startDate, end_date: endDate });
    commit('SET_DATE_RANGE_PRESET', preset);
    if (state.drillDownLevel === 3) {
      await dispatch('fetchDetailsData', 1);
    } else {
      await dispatch('fetchSummaryData');
    }
  },

  async clearAllFilters({ commit, dispatch }) {
    commit('RESET_FILTERS');
    commit('RESET_DRILL_DOWN');
    commit('SET_DATE_RANGE', { start_date: null, end_date: null });
    commit('SET_DATE_RANGE_PRESET', 'last30days');
    await dispatch('applyDateRangePreset', 'last30days');
    await dispatch('fetchSummaryData');
  },

  async changePage({ dispatch }, page) {
    await dispatch('fetchDetailsData', page);
  },
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
